import axios, { CancelToken } from 'axios';
import axiosRetry from 'axios-retry';

const SPOTIFY_CLIENT_ID = '068c0eb38bd249cd82d1097fae9e0644';
const SPOTIFY_CLIENT_SECRET = '070b7a269c9040aea67420066725096a';

// One shared instance so retry behavior is configured in a single place.
// Spotify signals rate limiting with 429 + a Retry-After header (in seconds),
// which the default axios-retry condition does not cover.
const axiosInstance = axios.create();
axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: (retryCount, error) => {
    const retryAfter = Number(error.response?.headers?.['retry-after']);
    if (retryAfter > 0) {
      // cap the wait so an interactive request never stalls for minutes
      return Math.min(retryAfter * 1000, 30000);
    }
    return axiosRetry.exponentialDelay(retryCount);
  },
  retryCondition: error =>
    axiosRetry.isNetworkOrIdempotentRequestError(error) || error.response?.status === 429
});

let cachedToken: string | null = null;
let tokenExpiresAt = 0;
let tokenExchange: Promise<void> | null = null;

const exchangeToken = async (): Promise<void> => {
  const res = await axios.post(
    'https://accounts.spotify.com/api/token',
    new URLSearchParams({ grant_type: 'client_credentials' }),
    {
      headers: {
        Authorization: `Basic ${btoa(`${SPOTIFY_CLIENT_ID}:${SPOTIFY_CLIENT_SECRET}`)}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }
  );
  cachedToken = res.data.access_token;
  // refresh 5 minutes before the 1-hour expiry
  tokenExpiresAt = Date.now() + (res.data.expires_in - 300) * 1000;
};

const getSpotifyAccessToken = async (): Promise<{ headers: { Authorization: string } }> => {
  if (!cachedToken || Date.now() >= tokenExpiresAt) {
    // concurrent callers share one exchange instead of each requesting a token
    if (!tokenExchange) {
      tokenExchange = exchangeToken().finally(() => {
        tokenExchange = null;
      });
    }
    await tokenExchange;
  }
  return {
    headers: {
      Authorization: `Bearer ${cachedToken}`
    }
  };
};

const getSearchArtistsEndpoint = (query: string) => {
  // limit=50 is the search API's maximum — generically-named artists
  // (e.g. "Victoria") often rank below the top 10 by popularity
  return `https://api.spotify.com/v1/search?q=${encodeURIComponent(query)}&type=artist&limit=50`;
};

const getArtistEndpoint = (artistId: string) => {
  return `https://api.spotify.com/v1/artists/${artistId}`;
};

const getArtistAlbumsEndpoint = (artistId: string, offset = 0, market = 'US') => {
  return `https://api.spotify.com/v1/artists/${artistId}/albums?limit=${50}&offset=${offset}&market=${market}&include_groups=album,single,compilation`;
};

const getAlbumTracksEndpoint = (albumId: string, offset = 0, market = 'US') => {
  return `https://api.spotify.com/v1/albums/${albumId}/tracks?limit=${50}&offset=${offset}&market=${market}`;
};

const getSeveralTracksEndpoint = (trackIds: string) => {
  return `https://api.spotify.com/v1/tracks?ids=${trackIds}`;
};

const getSeveralAlbumsEndpoint = (albumIds: string) => {
  // market=US so the embedded track listings are the same US-relinked
  // variants (and ISRCs) the old per-album track calls returned — and the
  // same ones the createScheduleA lambda matches checked tracks against
  return `https://api.spotify.com/v1/albums?ids=${albumIds}&market=US`;
};

export const getSeveralAlbums = async (albumIds: string) => {
  try {
    const header = await getSpotifyAccessToken();
    return await axiosInstance.get(getSeveralAlbumsEndpoint(albumIds), header);
  } catch (error) {
    return 'error';
  }
};

export const getSeveralTracks = async (trackIds: string) => {
  try {
    const header = await getSpotifyAccessToken();
    return await axiosInstance.get(getSeveralTracksEndpoint(trackIds), header);
  } catch (error) {
    return 'error';
  }
};

export const getArtistAlbums = async (artistId: string, offset = 0) => {
  try {
    const header = await getSpotifyAccessToken();
    return await axiosInstance.get(getArtistAlbumsEndpoint(artistId, offset), header);
  } catch (error) {
    return 'error';
  }
};

export const getAlbumTracks = async (albumId: string, offset = 0) => {
  try {
    const header = await getSpotifyAccessToken();
    return await axiosInstance.get(getAlbumTracksEndpoint(albumId, offset), header);
  } catch (error) {
    return 'error';
  }
};

// axios in this project (0.21.x) predates AbortController support, so
// cancellation uses the CancelToken API
export const createCancelSource = () => axios.CancelToken.source();

// An obscure artist with a generic name may never rank high enough to appear
// in a name search, but a pasted link always resolves. Recognizes share links
// (with optional intl-xx locale segment and ?si= tracking params),
// spotify:artist: URIs, and bare 22-char base62 IDs. A bare token is only
// probably an ID — it could be an unusual artist name — so it's marked
// non-explicit and name search remains the fallback if the lookup fails.
const parseArtistRef = (query: string): { id: string; explicit: boolean } | null => {
  const explicitMatch = query.match(
    /(?:open\.spotify\.com\/(?:intl-[a-z-]+\/)?artist\/|spotify:artist:)([0-9A-Za-z]{22})/
  );
  if (explicitMatch) {
    return { id: explicitMatch[1], explicit: true };
  }
  const bareMatch = query.trim().match(/^([0-9A-Za-z]{22})$/);
  return bareMatch ? { id: bareMatch[1], explicit: false } : null;
};

const searchArtistsByName = async (query: string, cancelToken?: CancelToken) => {
  try {
    const header = await getSpotifyAccessToken();
    return await axiosInstance.get(getSearchArtistsEndpoint(query), { ...header, cancelToken });
  } catch (error) {
    return null;
  }
};

export const searchArtists = async (query: string, cancelToken?: CancelToken) => {
  if (!query) {
    return null;
  }
  const ref = parseArtistRef(query);
  if (ref) {
    try {
      const header = await getSpotifyAccessToken();
      const result = await axiosInstance.get(getArtistEndpoint(ref.id), { ...header, cancelToken });
      // mirror the search response shape so callers don't special-case pasted links
      return { data: { artists: { items: [result.data] } } };
    } catch (error) {
      return ref.explicit ? null : searchArtistsByName(query, cancelToken);
    }
  }
  return searchArtistsByName(query, cancelToken);
};
